import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { listWorkspaces, ListWorkspacesParams } from '../api/client';
import type { WorkspaceListItem } from '../api/types';
import { buildListParams } from './buildListParams';

export function WorkspaceListPage() {
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamId, setTeamId] = useState('');
  const [q, setQ] = useState('');
  const [stripeCustomerId, setStripeCustomerId] = useState('');

  const fetchList = async (params: ListWorkspacesParams = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await listWorkspaces(params);
      setWorkspaces(res.workspaces ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load workspaces');
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchList(buildListParams(teamId, q, stripeCustomerId));
  };

  const clearFilters = () => {
    setTeamId('');
    setQ('');
    setStripeCustomerId('');
    fetchList();
  };

  return (
    <div style={styles.layout}>
      <header style={styles.header}>
        <h1 style={styles.title}>Workspaces</h1>
      </header>

      <section style={styles.filters}>
        <form onSubmit={handleSearch} style={styles.filterForm}>
          <label style={styles.label}>
            Team ID
            <input
              type="text"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              placeholder="T01234567"
              style={styles.input}
            />
          </label>
          <label style={styles.label}>
            Name / Slack workspace
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search"
              style={styles.input}
            />
          </label>
          <label style={styles.label}>
            Stripe customer ID
            <input
              type="text"
              value={stripeCustomerId}
              onChange={(e) => setStripeCustomerId(e.target.value)}
              placeholder="cus_..."
              style={styles.input}
            />
          </label>
          <div style={styles.actions}>
            <button type="submit" style={styles.primaryButton}>
              Search
            </button>
            <button type="button" onClick={clearFilters} style={styles.secondaryButton}>
              Clear
            </button>
          </div>
        </form>
      </section>

      {error && <div style={styles.error}>{error}</div>}

      {loading ? (
        <p style={styles.muted}>Loading…</p>
      ) : (
        <div style={styles.tableWrap}>
          <table>
            <thead>
              <tr>
                <th>Team ID</th>
                <th>Name</th>
                <th>Slack workspace</th>
                <th>Status</th>
                <th>Install date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {workspaces.length === 0 ? (
                <tr>
                  <td colSpan={6} style={styles.muted}>
                    No workspaces found.
                  </td>
                </tr>
              ) : (
                workspaces.map((w) => (
                  <tr key={w.teamId}>
                    <td>
                      <code style={styles.code}>{w.teamId}</code>
                    </td>
                    <td>{w.teamName ?? '—'}</td>
                    <td>{w.teamDomain ?? '—'}</td>
                    <td>{w.status ?? '—'}</td>
                    <td>{w.installDate ?? '—'}</td>
                    <td>
                      <Link to={`/workspaces/${encodeURIComponent(w.teamId)}`} style={styles.link}>
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  layout: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: 24,
  },
  header: {
    marginBottom: 24,
  },
  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 600,
    color: '#1a1a1a',
  },
  filters: {
    background: '#fff',
    padding: 20,
    borderRadius: 8,
    marginBottom: 24,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  filterForm: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    alignItems: 'flex-end',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 14,
    color: '#374151',
  },
  input: {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    minWidth: 160,
  },
  actions: {
    display: 'flex',
    gap: 8,
  },
  primaryButton: {
    padding: '8px 16px',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontWeight: 500,
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '8px 16px',
    background: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    cursor: 'pointer',
  },
  error: {
    padding: 12,
    background: '#fef2f2',
    color: '#b91c1c',
    borderRadius: 8,
    marginBottom: 16,
  },
  muted: {
    color: '#6b7280',
    padding: 16,
  },
  tableWrap: {
    background: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  code: {
    fontSize: 13,
    background: '#f3f4f6',
    padding: '2px 6px',
    borderRadius: 4,
  },
  link: {
    color: '#2563eb',
    fontWeight: 500,
  },
};
